import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useSEO from '../hooks/useSEO';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  FiSearch,
  FiCalendar,
  FiUser,
  FiTag,
  FiPlay,
  FiClock,
  FiEye,
  FiFilter,
  FiX
} from 'react-icons/fi';
import { FaVideo, FaNewspaper } from 'react-icons/fa';
import { getPublicBlogs } from '../utils/api';
import { getBlogImageUrl } from '../utils/imageUtils';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);

  // Search & Pagination states
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(6);

  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const heroRef = useRef(null);
  const blogListRef = useRef(null);

  useSEO({
    title: 'Healthy Living Blog & Articles',
    description: 'Read articles and healthy living tips written by expert nutritionist Deeksha Rajput. Insights on weight loss, PCOS management, dietary advice, and lifestyle tips.',
    keywords: 'nutrition blog, weight loss tips, healthy living articles, PCOS tips, fitness blog, Dfitclub blog',
  });
  const [heroInView] = useInView({ threshold: 0.1 });

  // Fetch blogs from API
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const data = await getPublicBlogs({ per_page: 50 });
        const blogs = data.blogs || [];
        setPosts(blogs);
        setFilteredPosts(blogs);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setPosts([]);
        setFilteredPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Filter and search functionality
  useEffect(() => {
    let filtered = posts;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Filter by type
    if (selectedType !== 'All') {
      filtered = filtered.filter(post => post.type === selectedType);
    }

    setFilteredPosts(filtered);
  }, [posts, searchTerm, selectedCategory, selectedType]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setVisibleCount(6);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setVisibleCount(6);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setVisibleCount(6);
  };

  const categories = ['All', 'Nutrition', 'Disease Management', 'Lifestyle', 'Meal Prep', 'Recipes'];
  const types = ['All', 'blog', 'vlog'];

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-r from-primary-50 to-primary-100 section-padding">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-6">
              Health & Nutrition
              <span className="text-visible-orange"> Blog</span>
            </h1>
            <p className="text-base text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Discover expert insights, practical tips, and inspiring stories to help you achieve
              your health and wellness goals. From nutrition science to meal prep hacks.
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <div className="text-2xl font-bold text-primary-600">{posts.length}+</div>
                <div className="text-neutral-600">Articles & Videos</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600">{categories.length - 1}</div>
                <div className="text-neutral-600">Categories</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600">
                  {posts.reduce((sum, post) => sum + (post.views || 0), 0) > 0
                    ? formatNumber(posts.reduce((sum, post) => sum + (post.views || 0), 0))
                    : '1k+'
                  }
                </div>
                <div className="text-neutral-600">Total Reads</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Posts */}
      {posts.filter(post => post.featured).length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-4">Featured Posts</h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                Hand-picked articles and videos that our community loves most
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.filter(post => post.featured).slice(0, 3).map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
                >
                  <div className="relative overflow-hidden">
                    <Link to={`/blog/${post.id}`}>
                      <img
                        src={getBlogImageUrl(post.image || post.thumbnail) || '/images/blog/default.jpg'}
                        alt={post.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </Link>
                    <div className="absolute top-4 left-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${post.type === 'vlog'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                        }`}>
                        {post.type === 'vlog' ? <FaVideo className="w-3 h-3" /> : <FaNewspaper className="w-3 h-3" />}
                        {post.type === 'vlog' ? 'Video' : 'Article'}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 text-xs font-medium rounded-full">
                        Featured
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-neutral-500 mb-3">
                      <span className="flex items-center gap-1">
                        <FiCalendar className="w-4 h-4" />
                        {formatDate(post.publish_date || post.publishDate)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FiEye className="w-4 h-4" />
                        {formatNumber(post.views || 0)}
                      </span>
                    </div>

                    <h3 className="text-xl font-semibold text-neutral-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {post.title}
                    </h3>

                    <p className="text-neutral-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-primary-600 font-medium">{post.category}</span>
                        <span className="text-neutral-300">•</span>
                        <span className="text-sm text-neutral-500">
                          {post.type === 'vlog' ? post.duration : post.read_time || post.readTime}
                        </span>
                      </div>

                      <Link
                        to={`/blog/${post.id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all"
                      >
                        Read More
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search and Filter Section */}
      <section ref={blogListRef} className="section-padding bg-neutral-50">
        <div className="container-max">
          <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
            <form onSubmit={handleSearchSubmit} className="flex flex-col lg:flex-row gap-4 items-center w-full">
              {/* Search */}
              <div className="flex-grow w-full relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search articles and videos..."
                  value={searchInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchInput(value);
                    setSearchTerm(value);
                    setVisibleCount(6);
                  }}
                  className="w-full pl-10 pr-10 py-3 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput('');
                      setSearchTerm('');
                      setVisibleCount(6);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Search Button */}
              <button
                type="submit"
                className="w-full lg:w-auto bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-md shadow-primary-500/20"
              >
                Search
              </button>

              {/* Filter Toggle for Mobile */}
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center justify-center gap-2 px-4 py-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors w-full"
              >
                <FiFilter className="w-5 h-5" />
                Filters
              </button>

              {/* Filters */}
              <div className={`flex flex-col sm:flex-row gap-4 w-full lg:w-auto ${showFilters ? 'flex' : 'hidden lg:flex'}`}>
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="px-4 py-3 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'All' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="px-4 py-3 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full"
                >
                  {types.map(type => (
                    <option key={type} value={type}>
                      {type === 'All' ? 'All Types' : type === 'vlog' ? 'Videos' : 'Articles'}
                    </option>
                  ))}
                </select>
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="mb-6">
            <p className="text-neutral-600">
              {loading ? 'Loading...' : `Showing ${Math.min(visibleCount, filteredPosts.length)} of ${filteredPosts.length} posts`}
            </p>
          </div>

          {/* Posts Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                  <div className="w-full h-48 bg-neutral-200"></div>
                  <div className="p-6">
                    <div className="h-4 bg-neutral-200 rounded mb-3"></div>
                    <div className="h-6 bg-neutral-200 rounded mb-3"></div>
                    <div className="h-4 bg-neutral-200 rounded mb-4"></div>
                    <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiSearch className="w-8 h-8 text-neutral-400" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">No posts found</h3>
              <p className="text-neutral-600 mb-6">
                Try adjusting your search terms or filters to find what you're looking for.
              </p>
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedType('All');
                  setVisibleCount(6);
                }}
                className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(0, visibleCount).map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all group"
                  >
                    <div className="relative overflow-hidden">
                      <Link to={`/blog/${post.id}`}>
                        <img
                          src={getBlogImageUrl(post.image || post.thumbnail) || '/images/blog/default.jpg'}
                          alt={post.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                      <div className="absolute top-4 left-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${post.type === 'vlog'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}>
                          {post.type === 'vlog' ? <FaVideo className="w-3 h-3" /> : <FaNewspaper className="w-3 h-3" />}
                          {post.type === 'vlog' ? 'Video' : 'Article'}
                        </span>
                      </div>
                      {post.featured && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 text-xs font-medium rounded-full">
                            Featured
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-neutral-500 mb-3">
                        <span className="flex items-center gap-1">
                          <FiUser className="w-4 h-4" />
                          {post.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <FiCalendar className="w-4 h-4" />
                          {formatDate(post.publish_date || post.publishDate)}
                        </span>
                        <span className="flex items-center gap-1">
                          <FiEye className="w-4 h-4" />
                          {formatNumber(post.views || 0)}
                        </span>
                      </div>

                      <h3 className="text-xl font-semibold text-neutral-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {post.title}
                      </h3>

                      <p className="text-neutral-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-primary-600 font-medium">{post.category}</span>
                        <span className="text-sm text-neutral-500">
                          {post.type === 'vlog' ? post.duration : post.read_time || post.readTime}
                        </span>
                      </div>

                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 3).map((tag, tagIndex) => (
                            <span key={tagIndex} className="px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <Link
                        to={`/blog/${post.id}`}
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm group-hover:gap-3 transition-all"
                      >
                        {post.type === 'vlog' ? 'Watch Video' : 'Read Article'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>

              {visibleCount < filteredPosts.length && (
                <div className="flex justify-center mt-12">
                  <button
                    onClick={() => setVisibleCount(prev => prev + 6)}
                    className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-bold rounded-xl shadow-lg hover:shadow-primary-500/25 transition-all duration-300 hover:scale-105 flex items-center gap-2"
                  >
                    See More
                    <svg className="w-4 h-4 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;